<template>
  <div>
    <p class="text-2xl mb-2">Crawlers Acquisition</p>

    <v-card class="mx-auto">
      <v-card-title class="text-h6 font-weight-regular justify-space-between">
        <span>{{ currentTitle }}</span>
        <v-avatar color="primary" size="24" v-text="step"></v-avatar>
      </v-card-title>

      <v-window v-model="step">
        <v-window-item :value="1">
          <v-card-text>
            <p class="mb-4">
              Sélectionnez le crawler à relancer, puis cliquez sur « Next ».
            </p>
            <v-data-table
              v-model="selected"
              :headers="headers"
              :items="workflows"
              item-key="key"
              single-select
              show-select
              hide-default-footer
              class="elevation-1"
            >
              <template v-slot:item.name="{ item }">
                <span class="primary--text font-weight-medium">
                  {{ item.name }}
                </span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-window-item>

        <v-window-item :value="2">
          <v-card-text>
            <v-card outlined class="pa-4">
              <p class="text-xl mb-4">Veuillez configurer les paramètres</p>

              <v-row>
                <v-col cols="12" md="6">
                  <v-radio-group v-model="periodSelectionType" row>
                    <v-radio
                      label="Pour un seul jour"
                      value="one-day"
                    ></v-radio>
                    <v-radio
                      label="Pour une période donnée"
                      value="a-period"
                    ></v-radio>
                  </v-radio-group>
                </v-col>

                <v-col cols="12" md="6">
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-if="periodSelectionType === 'one-day'"
                        v-model="day"
                        label="Date"
                        :prepend-icon="icons.mdiCalendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                      <v-text-field
                        v-else
                        :value="dateRangeLabel"
                        label="Dates"
                        :prepend-icon="icons.mdiCalendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>

                    <v-date-picker
                      v-if="periodSelectionType === 'one-day'"
                      v-model="day"
                      :max="maxDate"
                      @input="dateMenu = false"
                    ></v-date-picker>
                    <v-date-picker
                      v-else
                      v-model="dates"
                      :max="maxDate"
                      range
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>

              <template v-if="selectedWorkflowKey === 'daily-report'">
                <v-divider class="mb-3"></v-divider>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-radio-group v-model="platformSelectionType" row>
                      <v-radio
                        label="Pour toutes les plateformes"
                        value="all-platforms"
                      ></v-radio>
                      <v-radio
                        label="Choisir certaines plateformes"
                        value="select-platforms"
                      ></v-radio>
                    </v-radio-group>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-if="platformSelectionType === 'select-platforms'"
                      v-model="selectedPlatforms"
                      :items="platforms"
                      :menu-props="{ maxHeight: '400', zIndex: 13 }"
                      label="Sélectionner les plateformes"
                      outlined
                      multiple
                      chips
                      deletable-chips
                    ></v-autocomplete>
                  </v-col>
                </v-row>

                <v-divider class="mb-3"></v-divider>
                <v-checkbox
                  v-model="forceApi"
                  label="Force API calls (ne pas utiliser le cache)"
                ></v-checkbox>
              </template>

              <v-alert v-else type="info" text class="mt-3 mb-0">
                Le crawler UTM Content traitera tous les sites Actirise
                configurés et tous les UTM contents actifs.
              </v-alert>
            </v-card>
          </v-card-text>
        </v-window-item>

        <v-window-item :value="3">
          <v-card-text class="pa-6 text-center">
            <h3 class="text-h6 font-weight-light mb-4">
              Relancer le crawler <b>{{ selectedWorkflowName }}</b>
            </h3>

            <v-row>
              <v-col cols="12" md="8" offset-md="2" class="text-left">
                <ul>
                  <li>{{ periodSummary }}</li>
                  <li v-if="selectedWorkflowKey === 'daily-report'">
                    Plateformes : {{ selectedPlatformsLabel }}
                  </li>
                  <li v-if="selectedWorkflowKey === 'daily-report'">
                    Force API : {{ forceApi ? "Oui" : "Non" }}
                  </li>
                  <li v-else>
                    Tous les sites Actirise configurés et UTM contents actifs
                  </li>
                </ul>
              </v-col>
            </v-row>

            <v-btn
              color="primary"
              class="mt-6"
              :loading="isLaunching"
              :disabled="isLaunching"
              @click="triggerCrawler"
            >
              Relancer le crawler
            </v-btn>
          </v-card-text>
        </v-window-item>
      </v-window>

      <v-divider></v-divider>
      <v-card-actions>
        <v-btn v-if="step > 1" text @click="step--">Back</v-btn>
        <v-spacer></v-spacer>
        <v-btn v-if="step < 3" color="primary" @click="gotoNextStep">
          Next
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import axios from "@axios";
import { mdiCalendar } from "@mdi/js";

const DAILY_REPORT_PLATFORMS = [
  "Actirise",
  "GA",
  "Taboola",
  "Wyzistat",
  "Viously",
];

export default {
  name: "AcquisitionCrawlers",
  data() {
    return {
      step: 1,
      selected: [],
      isLaunching: false,
      periodSelectionType: "one-day",
      platformSelectionType: "all-platforms",
      forceApi: false,
      dateMenu: false,
      day: this.formatDate(this.daysAgo(1)),
      dates: [
        this.formatDate(this.daysAgo(3)),
        this.formatDate(this.daysAgo(1)),
      ],
      platforms: DAILY_REPORT_PLATFORMS,
      selectedPlatforms: [],
      workflows: [
        {
          key: "daily-report",
          name: "Daily Report",
          description:
            "Relance les crawlers qui alimentent le rapport Acquisition Daily Report.",
        },
        {
          key: "utm-content",
          name: "UTM Content",
          description:
            "Relance le crawler Actirise qui alimente le rapport UTM Content.",
        },
      ],
      headers: [
        {
          text: "Crawler",
          align: "start",
          sortable: true,
          value: "name",
        },
        {
          text: "Description",
          align: "start",
          sortable: false,
          value: "description",
        },
      ],
      icons: { mdiCalendar },
    };
  },
  computed: {
    selectedWorkflow() {
      return this.selected.length ? this.selected[0] : null;
    },
    selectedWorkflowKey() {
      return this.selectedWorkflow ? this.selectedWorkflow.key : "";
    },
    selectedWorkflowName() {
      return this.selectedWorkflow ? this.selectedWorkflow.name : "";
    },
    currentTitle() {
      if (this.step === 1) return "Sélectionner un crawler";
      if (this.step === 2) {
        return `Configurer le crawler ${this.selectedWorkflowName}`;
      }
      return "Récapitulatif et lancement";
    },
    maxDate() {
      return this.formatDate(this.daysAgo(1));
    },
    normalizedDates() {
      return [...this.dates].sort();
    },
    dateRangeLabel() {
      return this.normalizedDates.join(" - ");
    },
    dateStart() {
      return this.periodSelectionType === "one-day"
        ? this.day
        : this.normalizedDates[0];
    },
    dateEnd() {
      return this.periodSelectionType === "one-day"
        ? this.day
        : this.normalizedDates[1];
    },
    formErrors() {
      const errors = [];
      if (this.periodSelectionType === "one-day" && !this.day) {
        errors.push("Veuillez sélectionner une date.");
      }
      if (
        this.periodSelectionType === "a-period" &&
        this.dates.length !== 2
      ) {
        errors.push("Veuillez sélectionner une période complète.");
      }
      if (
        this.selectedWorkflowKey === "daily-report" &&
        this.platformSelectionType === "select-platforms" &&
        this.selectedPlatforms.length === 0
      ) {
        errors.push("Veuillez sélectionner au moins une plateforme.");
      }
      return errors;
    },
    effectivePlatforms() {
      return this.platformSelectionType === "all-platforms"
        ? this.platforms
        : this.selectedPlatforms;
    },
    selectedPlatformsLabel() {
      return this.effectivePlatforms.join(", ");
    },
    periodSummary() {
      if (this.dateStart === this.dateEnd) {
        return `Pour la journée du ${this.dateStart}`;
      }
      return `Pour la période du ${this.dateStart} au ${this.dateEnd}`;
    },
  },
  methods: {
    daysAgo(numberOfDays) {
      const value = new Date();
      value.setDate(value.getDate() - numberOfDays);
      return value;
    },
    formatDate(value) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const day = String(value.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    async showError(message) {
      await this.$store.dispatch("app/setSnackBar", {
        show: true,
        text: message,
        color: "warning",
        timeout: 5000,
      });
    },
    async gotoNextStep() {
      if (this.step === 1 && !this.selectedWorkflow) {
        await this.showError("Veuillez sélectionner un crawler.");
        return;
      }
      if (this.step === 2 && this.formErrors.length) {
        await this.showError(this.formErrors.join(" "));
        return;
      }
      this.step += 1;
    },
    async triggerCrawler() {
      const isDailyReport = this.selectedWorkflowKey === "daily-report";
      const endpoint = isDailyReport
        ? "/acquisition/report/trigger-crawler"
        : "/acquisition/utm-content/report/trigger-crawler";
      const payload = {
        date_start: this.dateStart,
        date_end: this.dateEnd,
      };

      if (isDailyReport) {
        payload.platforms = this.effectivePlatforms;
        payload.force_api = this.forceApi;
      }

      this.isLaunching = true;
      try {
        await axios.post(endpoint, payload);
        await this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: `Crawler ${this.selectedWorkflowName} relancé avec succès !`,
          color: "success",
          timeout: 5000,
        });
        this.step = 1;
        this.selected = [];
      } catch (error) {
        const responseData = error.response && error.response.data;
        const detail = responseData && responseData.detail;
        await this.showError(
          detail || `Erreur lors du lancement du crawler ${this.selectedWorkflowName}.`
        );
      } finally {
        this.isLaunching = false;
      }
    },
  },
};
</script>
